import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Calendar as CalendarComponent } from '../ui/calendar';
import {
  Users, Truck, Loader2, ChevronsUpDown, Check, X, Plus, UserPlus,
  AlertCircle, Bell, Calendar, Construction,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { cn } from '../../lib/utils';
import NotifyWorkersDialog from '../NotifyWorkersDialog';
import LeaveOverrideDialog from '../LeaveOverrideDialog';
import { getLeaveBlock, withLeaveOverride } from '../../lib/leaveOverride';

function formatDateShort(dateStr) {
  if (!dateStr) return '-';
  try { return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'dd MMM yy'); } catch { return dateStr; }
}

export function TowerCraneAllocations({ jobId, jobData }) {
  const { api } = useAuth();
  const [workerAllocations, setWorkerAllocations] = useState([]);
  const [assetAllocations, setAssetAllocations] = useState([]);
  const [primaryAllocation, setPrimaryAllocation] = useState(null);
  const [operators, setOperators] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add worker form
  const [addingWorker, setAddingWorker] = useState(false);
  const [newWorkerTypeId, setNewWorkerTypeId] = useState('');
  const [newOperatorId, setNewOperatorId] = useState('');
  const [newWorkerStart, setNewWorkerStart] = useState('');
  const [newWorkerEnd, setNewWorkerEnd] = useState('');
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [workerStartOpen, setWorkerStartOpen] = useState(false);
  const [workerEndOpen, setWorkerEndOpen] = useState(false);
  const [savingWorker, setSavingWorker] = useState(false);
  const [tcaLeaveOverride, setTcaLeaveOverride] = useState({ open: false, blocks: [], callback: null });

  // Add support asset form
  const [addingAsset, setAddingAsset] = useState(false);
  const [newEquipmentId, setNewEquipmentId] = useState('');
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [savingAsset, setSavingAsset] = useState(false);

  // Primary crane allocation form
  const [addingCrane, setAddingCrane] = useState(false);
  const [newCraneEquipmentId, setNewCraneEquipmentId] = useState('');
  const [craneEquipOpen, setCraneEquipOpen] = useState(false);
  const [savingCrane, setSavingCrane] = useState(false);

  // Notify workers
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [jobForNotify, setJobForNotify] = useState(null);

  const defaultStart = jobData?.start_date || new Date().toISOString().split('T')[0];
  const defaultEnd = jobData?.expected_off_hire_date || jobData?.end_date || '';

  const fetchAllocations = useCallback(async () => {
    if (!jobId) return;
    try {
      const [waRes, saRes, mainRes] = await Promise.all([
        api.get(`/worker-allocations/job/${jobId}`),
        api.get(`/allocations/${jobId}/support-assets`),
        api.get(`/jobs/${jobId}/allocations`).catch(() => ({ data: { allocations: [] } })),
      ]);
      setWorkerAllocations(waRes.data || []);
      setAssetAllocations(saRes.data || []);
      // Primary allocation = first non-support-asset allocation from the job's allocations
      const allAllocations = mainRes.data?.allocations || [];
      const primary = Array.isArray(allAllocations)
        ? allAllocations.find(a => !a.is_support_asset)
        : null;
      setPrimaryAllocation(primary || null);
    } catch {
      /* ignore */
    }
  }, [api, jobId]);

  useEffect(() => {
    const load = async () => {
      try {
        const [opRes, wtRes, eqRes] = await Promise.all([
          api.get('/operators'),
          api.get('/worker-types'),
          api.get('/equipment?status=active'),
        ]);
        setOperators(opRes.data || []);
        setWorkerTypes(wtRes.data || []);
        setEquipment(eqRes.data || []);
      } catch { /* ignore */ }
      await fetchAllocations();
      setLoading(false);
    };
    load();
  }, [api, fetchAllocations]);

  // ── Missing allocation indicators ──
  const missingWorkers = useMemo(() => {
    const requirements = jobData?.worker_requirements || [];
    if (requirements.length === 0) return [];
    const missing = [];
    for (const req of requirements) {
      const wtName = workerTypes.find(wt => wt.id === req.worker_type_id)?.name || req.worker_type_id;
      const allocated = workerAllocations.filter(wa => wa.worker_type_id === req.worker_type_id).length;
      const needed = req.quantity || 1;
      if (allocated < needed) {
        missing.push({ worker_type_id: req.worker_type_id, name: wtName, needed, allocated, shortfall: needed - allocated });
      }
    }
    return missing;
  }, [jobData?.worker_requirements, workerAllocations, workerTypes]);

  const totalMissing = missingWorkers.reduce((s, m) => s + m.shortfall, 0);

  // ── Handlers ──
  const resetWorkerForm = () => {
    setAddingWorker(false);
    setNewWorkerTypeId('');
    setNewOperatorId('');
    setNewWorkerStart('');
    setNewWorkerEnd('');
  };

  const handleAddWorker = async (leaveOverrideReason = null) => {
    if (!newWorkerTypeId || !newOperatorId) {
      toast.error('Select both a worker type and operator');
      return;
    }
    setSavingWorker(true);
    try {
      const url = withLeaveOverride('/worker-allocations', leaveOverrideReason);
      await api.post(url, {
        job_id: jobId,
        worker_type_id: newWorkerTypeId,
        operator_id: newOperatorId,
        start_date: newWorkerStart || defaultStart,
        end_date: newWorkerEnd || defaultEnd || null,
      });
      toast.success('Worker allocated');
      resetWorkerForm();
      await fetchAllocations();
    } catch (err) {
      const lb = getLeaveBlock(err);
      if (lb && !leaveOverrideReason) {
        setTcaLeaveOverride({
          open: true,
          blocks: [lb],
          callback: (reason) => handleAddWorker(reason),
        });
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to allocate worker');
    } finally {
      setSavingWorker(false);
    }
  };

  const handleRemoveWorker = async (allocationId) => {
    try {
      await api.delete(`/worker-allocations/${allocationId}`);
      toast.success('Worker allocation removed');
      await fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove allocation');
    }
  };

  const handleAddAsset = async () => {
    if (!newEquipmentId) { toast.error('Select equipment'); return; }
    setSavingAsset(true);
    try {
      await api.post(`/allocations/${jobId}/support-asset`, {
        equipment_id: newEquipmentId,
        start_date: defaultStart,
        end_date: defaultEnd || null,
      });
      toast.success('Asset allocated');
      setAddingAsset(false);
      setNewEquipmentId('');
      await fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to allocate asset');
    } finally {
      setSavingAsset(false);
    }
  };

  const handleRemoveAsset = async (allocationId) => {
    try {
      await api.delete(`/allocations/${allocationId}`);
      toast.success('Asset allocation removed');
      await fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove allocation');
    }
  };

  // ── Primary Crane Allocation ──
  const handleAllocateCrane = async () => {
    if (!newCraneEquipmentId) { toast.error('Select a crane'); return; }
    setSavingCrane(true);
    try {
      await api.post('/allocations', {
        job_id: jobId,
        equipment_id: newCraneEquipmentId,
        operator_id: '',
        start_date: defaultStart,
        end_date: defaultEnd || null,
      });
      toast.success('Tower crane allocated');
      setAddingCrane(false);
      setNewCraneEquipmentId('');
      await fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to allocate crane');
    } finally {
      setSavingCrane(false);
    }
  };

  const handleRemovePrimary = async () => {
    if (!primaryAllocation?.id) return;
    try {
      await api.delete(`/allocations/${primaryAllocation.id}`);
      toast.success('Crane allocation removed');
      await fetchAllocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove allocation');
    }
  };

  // ── Notify ──
  const handleNotifyWorkers = () => {
    setJobForNotify({
      id: jobId,
      job_number: jobData?.job_number || '',
      ...jobData,
    });
    setNotifyOpen(true);
  };

  const getWorkerTypeName = (typeId) => workerTypes.find(wt => wt.id === typeId)?.name || typeId;
  const getOperatorName = (opId) => operators.find(o => o.id === opId)?.name || opId;

  const statusColor = (status) => {
    if (status === 'confirmed') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    if (status === 'draft') return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
    return 'bg-muted text-muted-foreground';
  };

  // Crane-type equipment for the primary allocation picker
  const craneEquipment = useMemo(() => {
    const craneType = jobData?.equipment_type || 'Tower Crane';
    return equipment.filter(e =>
      (e.equipment_type || '').toLowerCase().includes('crane') ||
      (e.equipment_type || '').toLowerCase().includes(craneType.toLowerCase())
    );
  }, [equipment, jobData?.equipment_type]);

  const eqLabel = (eq) => eq.name || eq.equipment_type || eq.asset_id || 'Equipment';

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card data-testid="tc-allocations-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" /> Allocations
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {totalMissing > 0 && (
              <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-400 border-red-500/30">
                {totalMissing} missing
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {workerAllocations.length} worker{workerAllocations.length !== 1 ? 's' : ''}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs gap-1"
              onClick={handleNotifyWorkers}
              disabled={workerAllocations.length === 0}
              data-testid="tc-notify-workers-btn"
            >
              <Bell className="h-3 w-3" /> Notify
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* ── Primary Crane Allocation ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Construction className="h-3.5 w-3.5" /> Tower Crane
            </h4>
          </div>

          {primaryAllocation ? (
            <div
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              data-testid="tc-primary-crane-alloc"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Construction className="h-3.5 w-3.5 text-[#ffd108] shrink-0" />
                <span className="font-medium truncate">{primaryAllocation.equipment_name || 'Crane'}</span>
                {primaryAllocation.equipment_asset_id && (
                  <span className="text-[10px] text-muted-foreground">({primaryAllocation.equipment_asset_id})</span>
                )}
                <Badge variant="outline" className={cn("text-[10px] shrink-0", statusColor(primaryAllocation.status))}>
                  {primaryAllocation.status || 'draft'}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {formatDateShort(primaryAllocation.start_date)} — {formatDateShort(primaryAllocation.end_date)}
                </span>
              </div>
              <Button
                variant="ghost" size="icon"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={handleRemovePrimary}
                data-testid="tc-remove-primary-crane"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : !addingCrane ? (
            <div
              className="border border-dashed border-amber-500/40 bg-amber-500/5 rounded-md p-3 text-center cursor-pointer hover:bg-amber-500/10 transition-colors"
              onClick={() => setAddingCrane(true)}
              data-testid="tc-allocate-crane-prompt"
            >
              <Construction className="h-5 w-5 mx-auto text-amber-400 mb-1" />
              <p className="text-xs text-amber-400 font-medium">No crane allocated</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Click to assign a tower crane to this job</p>
            </div>
          ) : null}

          {/* Add crane form */}
          {addingCrane && (
            <div className="border rounded-md p-3 space-y-2 bg-muted/20 mt-2" data-testid="tc-add-crane-form">
              <div>
                <Label className="text-[10px] text-muted-foreground">Crane *</Label>
                <Popover open={craneEquipOpen} onOpenChange={setCraneEquipOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between h-8 text-xs font-normal" data-testid="tc-new-crane-equipment">
                      {newCraneEquipmentId ? (() => { const eq = equipment.find(e => e.id === newCraneEquipmentId); return eq ? eqLabel(eq) : 'Select...'; })() : 'Select crane...'}
                      <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search cranes..." />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty>No crane found.</CommandEmpty>
                        {craneEquipment.length > 0 && (
                          <CommandGroup heading="Tower Cranes">
                            {craneEquipment.map(eq => (
                              <CommandItem key={eq.id} value={`${eqLabel(eq)} ${eq.asset_id || ''}`} onSelect={() => { setNewCraneEquipmentId(eq.id); setCraneEquipOpen(false); }}>
                                <Check className={cn('mr-2 h-3 w-3', newCraneEquipmentId === eq.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="truncate">{eqLabel(eq)}</span>
                                {eq.asset_id && <span className="text-[10px] text-muted-foreground ml-1">({eq.asset_id})</span>}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        <CommandGroup heading="All Equipment">
                          {equipment.filter(e => !craneEquipment.some(c => c.id === e.id)).map(eq => (
                            <CommandItem key={eq.id} value={`${eqLabel(eq)} ${eq.asset_id || ''} ${eq.equipment_type || ''}`} onSelect={() => { setNewCraneEquipmentId(eq.id); setCraneEquipOpen(false); }}>
                              <Check className={cn('mr-2 h-3 w-3', newCraneEquipmentId === eq.id ? 'opacity-100' : 'opacity-0')} />
                              <span className="truncate">{eqLabel(eq)}</span>
                              {eq.asset_id && <span className="text-[10px] text-muted-foreground ml-1">({eq.asset_id})</span>}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAddingCrane(false); setNewCraneEquipmentId(''); }}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs bg-[#ffd108] text-black hover:bg-[#e5bc07]" onClick={handleAllocateCrane} disabled={savingCrane} data-testid="tc-confirm-add-crane">
                  {savingCrane ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Allocate Crane
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Divider ── */}
        <div className="border-t border-border" />

        {/* ── Worker Allocations ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <UserPlus className="h-3.5 w-3.5" /> Workers
            </h4>
            {!addingWorker && (
              <Button
                variant="ghost" size="sm" className="h-6 text-xs text-[#ffd108]"
                onClick={() => { setAddingWorker(true); setNewWorkerStart(defaultStart); setNewWorkerEnd(defaultEnd); }}
                data-testid="tc-alloc-add-worker-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Worker
              </Button>
            )}
          </div>

          {/* Missing allocation indicators */}
          {missingWorkers.length > 0 && (
            <div className="space-y-1 mb-2" data-testid="tc-missing-workers">
              {missingWorkers.map(m => (
                <div key={m.worker_type_id} className="flex items-center gap-2 text-xs rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-1.5">
                  <AlertCircle className="h-3 w-3 text-amber-400 shrink-0" />
                  <span className="text-amber-400">
                    <strong>{m.name}</strong>: {m.allocated}/{m.needed} allocated
                    <span className="text-amber-400/60 ml-1">({m.shortfall} more needed)</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {workerAllocations.length === 0 && !addingWorker && missingWorkers.length === 0 && (
            <div className="text-xs text-muted-foreground italic border border-dashed rounded-md p-3 text-center">
              No workers allocated yet
            </div>
          )}

          {workerAllocations.length > 0 && (
            <div className="space-y-1.5">
              {workerAllocations.map(wa => (
                <div
                  key={wa.id}
                  className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                  data-testid={`tc-worker-alloc-${wa.id}`}
                >
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <span className="font-medium truncate">{getOperatorName(wa.operator_id)}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{getWorkerTypeName(wa.worker_type_id)}</Badge>
                    <Badge variant="outline" className={cn("text-[10px] shrink-0", statusColor(wa.status))}>
                      {wa.status || 'draft'}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {formatDateShort(wa.start_date)} — {formatDateShort(wa.end_date)}
                    </span>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveWorker(wa.id)}
                    data-testid={`tc-remove-worker-${wa.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add worker form */}
          {addingWorker && (
            <div className="border rounded-md p-3 space-y-2.5 bg-muted/20 mt-2" data-testid="tc-add-worker-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Worker Type *</Label>
                  <Select value={newWorkerTypeId || 'none'} onValueChange={v => setNewWorkerTypeId(v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="tc-new-worker-type">
                      <SelectValue placeholder="Worker type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Select type...</SelectItem>
                      {workerTypes.map(wt => (
                        <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Operator *</Label>
                  <Popover open={operatorOpen} onOpenChange={setOperatorOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between h-8 text-xs font-normal" data-testid="tc-new-worker-operator">
                        {newOperatorId ? operators.find(o => o.id === newOperatorId)?.name || 'Select...' : 'Select operator...'}
                        <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search operators..." />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No operator found.</CommandEmpty>
                          <CommandGroup>
                            {operators.map(op => (
                              <CommandItem key={op.id} value={`${op.name} ${op.operator_code || ''}`} onSelect={() => { setNewOperatorId(op.id); setOperatorOpen(false); }}>
                                <Check className={cn('mr-2 h-3 w-3', newOperatorId === op.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="truncate">{op.name}</span>
                                {op.operator_code && <span className="text-[10px] text-muted-foreground ml-1">({op.operator_code})</span>}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {/* Date range row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Start Date</Label>
                  <Popover open={workerStartOpen} onOpenChange={setWorkerStartOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start h-8 text-xs font-normal" data-testid="tc-new-worker-start-date">
                        <Calendar className="mr-1.5 h-3 w-3" />
                        {newWorkerStart ? formatDateShort(newWorkerStart) : 'Pick start'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={newWorkerStart ? parse(newWorkerStart, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={d => { if (d) setNewWorkerStart(format(d, 'yyyy-MM-dd')); setWorkerStartOpen(false); }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">End Date</Label>
                  <Popover open={workerEndOpen} onOpenChange={setWorkerEndOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start h-8 text-xs font-normal" data-testid="tc-new-worker-end-date">
                        <Calendar className="mr-1.5 h-3 w-3" />
                        {newWorkerEnd ? formatDateShort(newWorkerEnd) : 'Open-ended'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={newWorkerEnd ? parse(newWorkerEnd, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={d => { if (d) setNewWorkerEnd(format(d, 'yyyy-MM-dd')); setWorkerEndOpen(false); }}
                      />
                      {newWorkerEnd && (
                        <div className="p-2 border-t">
                          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setNewWorkerEnd(''); setWorkerEndOpen(false); }}>
                            Clear (open-ended)
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={resetWorkerForm}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs bg-[#ffd108] text-black hover:bg-[#e5bc07]" onClick={handleAddWorker} disabled={savingWorker} data-testid="tc-confirm-add-worker">
                  {savingWorker ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Allocate
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Divider ── */}
        <div className="border-t border-border" />

        {/* ── Support Asset Allocations ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5" /> Support Assets
            </h4>
            {!addingAsset && (
              <Button
                variant="ghost" size="sm" className="h-6 text-xs text-[#ffd108]"
                onClick={() => setAddingAsset(true)}
                data-testid="tc-alloc-add-asset-btn"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Asset
              </Button>
            )}
          </div>

          {assetAllocations.length === 0 && !addingAsset && (
            <div className="text-xs text-muted-foreground italic border border-dashed rounded-md p-3 text-center">
              No support assets allocated
            </div>
          )}

          {assetAllocations.length > 0 && (
            <div className="space-y-1.5">
              {assetAllocations.map(sa => (
                <div key={sa.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`tc-asset-alloc-${sa.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">{sa.equipment_name || 'Unknown'}</span>
                    {sa.equipment_asset_id && <span className="text-[10px] text-muted-foreground">({sa.equipment_asset_id})</span>}
                    {sa.equipment_type && <Badge variant="outline" className="text-[10px] shrink-0">{sa.equipment_type}</Badge>}
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveAsset(sa.id)}
                    data-testid={`tc-remove-asset-${sa.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {addingAsset && (
            <div className="border rounded-md p-3 space-y-2 bg-muted/20 mt-2" data-testid="tc-add-asset-form">
              <Popover open={equipmentOpen} onOpenChange={setEquipmentOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between h-8 text-xs font-normal" data-testid="tc-new-asset-equipment">
                    {newEquipmentId ? (() => { const eq = equipment.find(e => e.id === newEquipmentId); return eq ? eqLabel(eq) : 'Select...'; })() : 'Select equipment...'}
                    <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search equipment..." />
                    <CommandList className="max-h-[200px]">
                      <CommandEmpty>No equipment found.</CommandEmpty>
                      <CommandGroup>
                        {equipment.map(eq => (
                          <CommandItem key={eq.id} value={`${eqLabel(eq)} ${eq.asset_id || ''} ${eq.equipment_type || ''}`} onSelect={() => { setNewEquipmentId(eq.id); setEquipmentOpen(false); }}>
                            <Check className={cn('mr-2 h-3 w-3', newEquipmentId === eq.id ? 'opacity-100' : 'opacity-0')} />
                            <span className="truncate">{eqLabel(eq)}</span>
                            {eq.asset_id && <span className="text-[10px] text-muted-foreground ml-1">({eq.asset_id})</span>}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAddingAsset(false); setNewEquipmentId(''); }}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs bg-[#ffd108] text-black hover:bg-[#e5bc07]" onClick={handleAddAsset} disabled={savingAsset} data-testid="tc-confirm-add-asset">
                  {savingAsset ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Allocate
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Notify Workers Dialog */}
    {notifyOpen && jobForNotify && (
      <NotifyWorkersDialog
        open={notifyOpen}
        onOpenChange={(o) => { if (!o) { setNotifyOpen(false); setJobForNotify(null); } }}
        api={api}
        jobId={jobId}
        job={jobForNotify}
        jobLabel={`Job #${jobForNotify.job_number || ''}`}
        onSent={fetchAllocations}
      />
    )}

    <LeaveOverrideDialog
      open={tcaLeaveOverride.open}
      blocks={tcaLeaveOverride.blocks}
      onCancel={() => setTcaLeaveOverride({ open: false, blocks: [], callback: null })}
      onOverride={(reason) => {
        const cb = tcaLeaveOverride.callback;
        setTcaLeaveOverride({ open: false, blocks: [], callback: null });
        if (cb) cb(reason);
      }}
    />
    </>
  );
}
